import { useState, useEffect } from 'react';
import { format } from 'date-fns';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Chip from '@/components/Chip';
import IconicButton from '@/components/IconicButton';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import LoadingState from '@/components/LoadingState';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type HelloSignDocument = {
  id: number;
  title: string;
  hellosign_id: string;
  request_type: string;
  request_status: string;
  migration_status: string;
  file_name: string | null;
  byte_size: number | null;
  migrated_at: string | null;
  source_completed_at: string | null;
  last_error: string | null;
  downloadable: boolean;
};

type Props = {
  workerId: number;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy');
  } catch {
    return dateString;
  }
};

const formatBytes = (bytes: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getMigrationChip = (status: string) => {
  switch (status) {
    case 'migrated':
      return <Chip color="success" label="Migrated" size="small" />;
    case 'pending':
      return <Chip color="warning" label="Pending" size="small" />;
    case 'failed':
      return <Chip color="error" label="Failed" size="small" />;
    case 'skipped':
      return <Chip label="Skipped" size="small" />;
    default:
      return <Chip label={status || 'Unknown'} size="small" />;
  }
};

const HelloSignDocumentsCard = ({ workerId }: Props) => {
  const [documents, setDocuments] = useState<HelloSignDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, [workerId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axiosRubyClient.get(
        '/api/admin/hellosign_document_audit',
        {
          params: {
            worker_id: workerId,
            migration_status: 'migrated',
            per_page: 100,
          },
        },
      );
      setDocuments(response.data.data || []);
      setError(null);
    } catch {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: HelloSignDocument) => {
    if (!doc.downloadable) return;
    setDownloading(doc.id);
    try {
      const response = await axiosRubyClient.get(
        `/api/admin/hellosign_document_audit/${doc.id}/download`,
      );
      if (response.data.success && response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
    } catch {
      setError('Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <Card title="HelloSign Documents">
        <Card.Section>
          <Stack align="center" justify="center" style={{ padding: '20px 0' }}>
            <LoadingState overlayColor="white" text="Loading documents" />
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card title="HelloSign Documents">
      <Card.Section>
        {error && (
          <Body color="error" style={{ marginBottom: '12px' }}>
            {error}
          </Body>
        )}
        {documents.length === 0 ? (
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">No HelloSign documents found</Small>
          </Stack>
        ) : (
          <Stack vertical gap={12}>
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <Stack vertical gap={4} style={{ flex: 1, minWidth: 0 }}>
                  <Body weight="medium">
                    {doc.title || 'Untitled Document'}
                  </Body>
                  <Stack gap={8} style={{ flexWrap: 'wrap' }}>
                    <Small color="lighter">
                      {doc.request_type?.replace(/_/g, ' ') || '-'}
                    </Small>
                    {doc.byte_size && (
                      <Small color="lighter">
                        {formatBytes(doc.byte_size)}
                      </Small>
                    )}
                    <Small color="lighter">
                      Signed: {formatDate(doc.source_completed_at)}
                    </Small>
                  </Stack>
                  <Stack gap={8} style={{ marginTop: '4px' }}>
                    {getMigrationChip(doc.migration_status)}
                    {doc.last_error && (
                      <Small color="error" title={doc.last_error}>
                        Error
                      </Small>
                    )}
                  </Stack>
                </Stack>
                {doc.downloadable && (
                  <div style={{ marginLeft: '12px', flexShrink: 0 }}>
                    <IconicButton
                      a11yLabel="Download document"
                      appearance="primary"
                      icon={faDownload}
                      size="sm"
                      disabled={downloading === doc.id}
                      onClick={() => handleDownload(doc)}
                    />
                  </div>
                )}
              </div>
            ))}
          </Stack>
        )}
      </Card.Section>
    </Card>
  );
};

export default HelloSignDocumentsCard;
